// libraries
import { useEffect } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import moment, { Moment } from 'moment'
import { FormInstance } from 'antd'
import { FieldData } from 'rc-field-form/lib/interface'

export function useFormFiller(formRef: React.RefObject<FormInstance<any>>): (values: Record<string, any>) => void {
	const location = useLocation()
	const history = useHistory()

	useEffect(() => {
		const formInstance: FormInstance | null = formRef.current
		if (formInstance === null) return

		const urlSearchParams: URLSearchParams = new URLSearchParams(location.search)
		const queryParams: Record<string, any> = Object.fromEntries(urlSearchParams)

		const formFieldValues: FieldData[] = Object.entries(queryParams).map(([name, value]: [string, string]) => {
			if (name.includes('filterTarget')) {
				const filterSuffix: string = name.replace('filterTarget', '')
				return { name: ['filter' + filterSuffix, 'target'], value }
			}

			if (name.includes('filterSearch')) {
				const filterSuffix: string = name.replace('filterSearch', '')
				return { name: ['filter' + filterSuffix, 'search'], value }
			}

			if (name === 'date') {
				const dates: (Moment | null)[] = value.split(',').map((date: string) => {
					return date === '' ? null : moment(date)
				})

				return { name: 'date', value: dates }
			}

			return { name, value }
		})

		formInstance.setFields(formFieldValues)
		formInstance.submit()
	}, [formRef, location.search])

	function setQueryParams(values: Record<string, any>): void {
		const urlSearchParams: URLSearchParams = new URLSearchParams()

		for (const key in values) {
			const value: any = values[key]
			if (value === undefined || value === null || value === '') continue

			if (key.includes('filter')) {
				const filterSuffix: string = key.replace('filter', '')
				if (value.target !== 'all') urlSearchParams.append('filterTarget' + filterSuffix, value.target)
				if (value.search !== '') urlSearchParams.append('filterSearch' + filterSuffix, value.search)
				continue
			}

			if (key === 'date') {
				const [dateFrom, dateUntil]: [Moment | null, Moment | null] = value
				if (dateFrom === null && dateUntil === null) {
					continue
				}

				const dates: (string | null)[] = []
				dates.push(dateFrom?.format('yyyy-MM-DD') ?? null)
				dates.push(dateUntil?.format('yyyy-MM-DD') ?? null)
				urlSearchParams.append('date', dates.join(','))
				continue
			}

			if (key === 'compact' && value === false) {
				continue
			}

			urlSearchParams.append(key, value)
		}

		const previousSearchParams = location.search.replace('?', '')

		if (previousSearchParams !== '' && previousSearchParams !== urlSearchParams.toString()) {
			return history.replace({ search: urlSearchParams.toString() })
		}

		history.replace({ hash: location.hash, search: urlSearchParams.toString() })
	}

	return setQueryParams
}

export const useFormQuery = (form: FormInstance) => {
	const location = useLocation()
	const history = useHistory()

	useEffect(() => {
		const urlSearchParams = new URLSearchParams(location.search)
		const queryParams = Object.fromEntries(urlSearchParams)

		const formFieldValues: FieldData[] = Object.entries(queryParams).map(([name, value]) => {
			if (name.includes('filterTarget')) {
				const filterSuffix = name.replace('filterTarget', '')
				return { name: ['filter' + filterSuffix, 'target'], value }
			}

			if (name.includes('filterSearch')) {
				const filterSuffix = name.replace('filterSearch', '')
				return { name: ['filter' + filterSuffix, 'search'], value }
			}

			if (name === 'date') {
				const dates = value.split(',').map((date) => (date === '' ? null : moment(date)))
				return { name: 'date', value: dates }
			}

			return { name, value }
		})

		form.setFields(formFieldValues)
		form.submit()
	}, [form, location.search])

	const setQueryParams = (values: Record<string, any>) => {
		const urlSearchParams = new URLSearchParams()

		for (const key in values) {
			const value = values[key]
			if (value === undefined || value === null || value === '') continue

			if (key.includes('filter')) {
				const filterSuffix: string = key.replace('filter', '')
				if (value.target !== 'all') urlSearchParams.append('filterTarget' + filterSuffix, value.target)
				if (value.search !== '') urlSearchParams.append('filterSearch' + filterSuffix, value.search)
				continue
			}

			if (key === 'date') {
				const [dateFrom, dateUntil]: [Moment | null, Moment | null] = value
				if (dateFrom === null && dateUntil === null) {
					continue
				}

				const dates: (string | null)[] = []
				dates.push(dateFrom?.format('yyyy-MM-DD') ?? null)
				dates.push(dateUntil?.format('yyyy-MM-DD') ?? null)
				urlSearchParams.append('date', dates.join(','))
				continue
			}

			if (key === 'compact' && value === false) {
				continue
			}

			urlSearchParams.append(key, value)
		}

		const previousSearchParams = location.search.replace('?', '')

		if (previousSearchParams !== '' && previousSearchParams !== urlSearchParams.toString()) {
			return history.replace({ search: urlSearchParams.toString() })
		}

		history.replace({ hash: location.hash, search: urlSearchParams.toString() })
	}

	return setQueryParams
}
